import { createEffect, createMemo, type JSX, on, onCleanup, onMount, Show } from "solid-js";
import { Outlet, useLocation, useNavigate, useRouterState } from "@tanstack/solid-router";
import type { AbstractPowerSyncDatabase } from "@powersync/common";

import { appState } from "@fleet/ui";
import { useSession } from "@/lib/auth";
import { appUndoStack } from "@/lib/undo";
import { useTheme } from "@/providers/theme-provider";
import { useSearchPalette } from "@/features/search/hooks/use-search-palette";

// Layout specific components and providers
import { AppBackground } from "@/components/app-background";
import { AppHeader } from "@/components/app-header";
import { OfflineBanner } from "@/components/offline-banner";
import { ListThemeProvider } from "@/providers/list-theme-provider";
import { CommandPalette } from "@/features/search/components/command-palette";

// Layout module parts
import { useInteractionFlag } from "@fleet/ui";
import { AppLayoutSidebar } from "./app-layout-sidebar";
import { AppLayoutDialogs } from "./app-layout-dialogs";
import { AccountSwitcherHost } from "@/features/auth/account-switcher-host";
import { useAppDialogs } from "../hooks/use-app-dialogs";
import { useAppShortcuts } from "../hooks/use-app-shortcuts";
import { useAppThemeState } from "../hooks/use-app-theme-state";

interface AppLayoutContentProps {
  db: AbstractPowerSyncDatabase | null;
}

// Drop `will-change` once the entrance finishes so we don't leave a persistent
// containing block (which would break `position: fixed` descendants / blur).
const handleEntranceEnd: JSX.EventHandler<HTMLDivElement, AnimationEvent> = (event) => {
  event.currentTarget.style.removeProperty("will-change");
};

function useGlobalEventListeners(
  isSidebarOpen: () => boolean,
  setIsSidebarOpen: (open: boolean) => void,
) {
  // Keyboard-vs-pointer activity tracking (and the `body.keyboard-navigation`
  // class) is owned by <InteractionProvider>; only the sidebar-specific Escape
  // handler remains here.
  onMount(() => {
    const handleEscapeForSidebar = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isSidebarOpen()) {
        setIsSidebarOpen(false);
      }
    };

    globalThis.addEventListener("keydown", handleEscapeForSidebar);

    onCleanup(() => {
      globalThis.removeEventListener("keydown", handleEscapeForSidebar);
    });
  });
}

export function AppLayoutContent(_props: AppLayoutContentProps) {
  void _props;
  onMount(() => {
    // Notify the InitialSyncOverlay that we are mounted and ready
    appState.markAsReady();
  });

  const navigate = useNavigate();
  const searchPalette = useSearchPalette();

  // L1 (undo): the undo stack is per active account. Switching accounts
  // clears it so a Ctrl+Z does not apply mutations from another account.
  const { userId } = useSession();
  createEffect(on(userId, () => appUndoStack.clear(), { defer: true }));

  const handleCloseSearch = () => {
    searchPalette.close();
  };

  const location = useLocation();
  const routerState = useRouterState();

  const isNavigating = () => routerState().status === "pending";

  // ── Canonical page-entrance key ─────────────────────────────────────────
  // The entrance animation runs ONCE per route SECTION. We key off the first
  // path segment so sub-routes (e.g. /profile/fonts, /admin/audit,
  // /organizations/$orgId) collapse onto their parent section and do NOT
  // re-trigger the slide-in when navigating within the same area. Search params
  // are excluded by construction (pathname only). This lives on a div SEPARATE
  // from any element that receives swipe/gesture transforms (POS ticket lines,
  // KDS bumps, the category carousel), so the page entrance never fights an
  // internal-state transform.
  const sectionKey = createMemo(() => {
    const segment = location().pathname.split("/").find(Boolean);
    return segment ?? "index";
  });

  const theme = useTheme();

  const { isSidebarOpen, setIsSidebarOpen, shortcutsOpen, setShortcutsOpen } =
    useAppDialogs(location);

  useAppShortcuts({
    searchPalette,
    location,
    navigate,
    theme,
    shortcutsOpen,
    setShortcutsOpen,
    setIsSidebarOpen,
  });

  // ── Keyboard-navigation visual indicator + touch gestures
  useGlobalEventListeners(isSidebarOpen, setIsSidebarOpen);

  // With the nav drawer open, the horizontal gesture belongs to it (drag to
  // dismiss). There is no window-bound page swipe in this repo yet, but the
  // flag is the ownership declaration either way: the day one is added, it
  // stands down for free.
  useInteractionFlag({ "nav-drawer.open": isSidebarOpen });

  const { activeBlur, activeBackground, glows } = useAppThemeState({ theme });

  return (
    <ListThemeProvider>
      <AppBackground
        class="flex h-dvh text-foreground overflow-hidden"
        isBlur={activeBlur()}
        background={activeBackground()}
        patternType={theme.tokens()?.patternType}
        patternOpacity={theme.tokens()?.patternOpacity}
        glow0={glows().glow0}
        glow1={glows().glow1}
        glow2={glows().glow2}
      >
        <AppLayoutSidebar isOpen={isSidebarOpen} setOpen={setIsSidebarOpen} />

        {/* ── Main content ───────────────────────────────────────── */}
        <main class="flex flex-1 flex-col overflow-hidden bg-transparent pt-[var(--safe-area-inset-top)] pr-[var(--safe-area-inset-right)] pb-[var(--safe-area-inset-bottom)]">
          <AppHeader onMenuClick={() => setIsSidebarOpen(true)} isNavigating={isNavigating} />
          <OfflineBanner />
          <div class="flex flex-1 flex-col min-h-0 overflow-hidden pr-[var(--safe-area-inset-right)] pl-[var(--safe-area-inset-left)]">
            {/* Keyed by route section so the entrance plays ONCE per area and
                re-creates the subtree (replaying slide-in) only on section
                change — never on intra-section navigation or search-param edits. */}
            <Show when={sectionKey()} keyed>
              <div
                class="flex flex-1 flex-col min-h-0 h-full motion-safe:animate-slide-in-up motion-safe:will-change-[transform,opacity]"
                onAnimationEnd={handleEntranceEnd}
              >
                <Outlet />
              </div>
            </Show>
          </div>
        </main>

        <AppLayoutDialogs shortcutsOpen={shortcutsOpen} setShortcutsOpen={setShortcutsOpen} />
      </AppBackground>

      {/* Command Palette — rendered OUTSIDE AppBackground to avoid blur compositor flash */}
      <CommandPalette isOpen={searchPalette.isOpen()} onClose={handleCloseSearch} />

      {/* Account switcher (multi-account #18) — mounted ONCE here, opened via the
          shared singleton from any sidebar instance. */}
      <AccountSwitcherHost />
    </ListThemeProvider>
  );
}
